import { useEffect, useRef, useState } from "react"

import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography
} from "@mui/material"

import type { LivePrompt } from "../livePrompts"
import AgentSection from "./AgentSection"
import CacheSection from "./CacheSection"
import {
  ACTOR_META,
  GUIDE_SECTIONS,
  GuideActor,
  GuideSectionId,
  JourneyStepId
} from "./guideContent"
import { ActorChip } from "./guideParts"
import JourneySection from "./JourneySection"
import PromptsSection from "./PromptsSection"
import { CompareSection, GlossarySection } from "./ReferenceSections"
import RulesSection, { DecidersSection } from "./RulesSection"
import ScenariosSection from "./ScenariosSection"
import SummarySection from "./SummarySection"

const LEGEND_ACTORS: GuideActor[] = ["customer", "api", "host", "agent"]

function sectionElement(id: GuideSectionId) {
  return document.getElementById(`ai-guide-${id}`)
}

function Contents({
  active,
  onJump
}: {
  active: GuideSectionId
  onJump: (id: GuideSectionId) => void
}) {
  return (
    <Box
      component="nav"
      sx={{
        position: "sticky",
        top: 24,
        display: { xs: "none", lg: "block" },
        width: 220,
        flex: "0 0 auto"
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", fontWeight: 800, px: 1.5 }}
      >
        Bölümler
      </Typography>
      <List dense disablePadding>
        {GUIDE_SECTIONS.map((section) => (
          <ListItemButton
            key={section.id}
            selected={section.id === active}
            onClick={() => onJump(section.id)}
            sx={{ borderRadius: 1 }}
          >
            <ListItemText
              primary={section.label}
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: section.id === active ? 800 : 500
              }}
            />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ my: 2 }} />
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", fontWeight: 800, px: 1.5 }}
      >
        Renkler
      </Typography>
      <Stack spacing={1} sx={{ px: 1.5, mt: 0.5 }}>
        {LEGEND_ACTORS.map((actor) => (
          <Box key={actor}>
            <ActorChip actor={actor} />
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", mt: 0.25 }}
            >
              {ACTOR_META[actor].description}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

export default function V2Guide({
  prompts,
  promptsLoading,
  promptsFailed
}: {
  prompts: LivePrompt[]
  promptsLoading: boolean
  promptsFailed: boolean
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeSection, setActiveSection] = useState<GuideSectionId>("summary")
  const [journeyStep, setJourneyStep] = useState<JourneyStepId>("arrive")

  useEffect(() => {
    const root = scrollRef.current
    if (!root || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        const id = visible[0]?.target.id.replace("ai-guide-", "")
        if (id) setActiveSection(id as GuideSectionId)
      },
      { root, rootMargin: "0px 0px -65% 0px" }
    )
    GUIDE_SECTIONS.forEach((section) => {
      const element = sectionElement(section.id)
      if (element) observer.observe(element)
    })
    return () => observer.disconnect()
  }, [])

  const jumpTo = (id: GuideSectionId) => {
    sectionElement(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const showJourneyStep = (id: JourneyStepId) => {
    setJourneyStep(id)
    jumpTo("journey")
  }

  return (
    <Box
      ref={scrollRef}
      sx={{ flex: 1, minHeight: 0, overflow: "auto", bgcolor: "#F7F8FA" }}
    >
      <Stack
        direction="row"
        spacing={4}
        alignItems="flex-start"
        sx={{ maxWidth: 1320, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}
      >
        <Contents active={activeSection} onJump={jumpTo} />
        <Stack spacing={7} sx={{ flex: 1, minWidth: 0, pb: 8 }}>
          <SummarySection />
          <JourneySection selected={journeyStep} onSelect={setJourneyStep} />
          <AgentSection />
          <DecidersSection />
          <RulesSection />
          <PromptsSection
            prompts={prompts}
            loading={promptsLoading}
            failed={promptsFailed}
          />
          <CacheSection />
          <ScenariosSection onJourneyStep={showJourneyStep} />
          <CompareSection />
          <GlossarySection />
        </Stack>
      </Stack>
    </Box>
  )
}
